import { Circle, Fill, Stroke, Style, RegularShape, Text } from 'ol/style';

import type { Options as StyleOptions } from 'ol/style/Style';

import Consts from '@/api/Consts';
import { appliedPrimaryColor } from '@/api/uiThemes';
import Utils from '@/api/Utils';

import MapConsts from '@/components/map/consts/MapConsts';
import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import MapHelpers from '@/components/map/utils/MapHelpers';

import type { BadgeStatusKey, ServiceKey } from '@/utils/types/baseStationsTypes';
import type {
	Baseline,
	BaseStationStyleFeatureData,
	ConnectedBaseStation,
	LonLat,
	MapMode,
	ServiceColors,
} from '@/components/map/types/mapFeatureTypes';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';


const STATUS_COLOR_KEYS = MapConsts.STATUS_COLORS satisfies Record<BadgeStatusKey, keyof typeof Consts.COLORS>;


const getStatusFillColor = (statusBadge: BadgeStatusKey): string => (
	Consts.COLORS[STATUS_COLOR_KEYS[statusBadge]]
);


const getTextStyle = (zIndex: number, text: string, offsetY: number, opacity = 1): Style => {
	const alphaHex = Math.round(opacity * 255).toString(16).padStart(2, '0');
	const textColor = opacity < 1 ? Consts.COLORS.dark0 + alphaHex : Consts.COLORS.dark0;
	const backgroundColor = opacity < 1 ? '#ffffff' + alphaHex : 'white';
	return new Style({
		zIndex: zIndex,
		text: new Text({
			text: text,
			offsetY: offsetY,
			fill: new Fill({
				color: textColor,
			}),
			backgroundFill: new Fill({
				color: backgroundColor,
			}),
			padding: [3, 2, 1, 3],
		}),
	});
};


const getLinkStyle = (colors: ServiceColors): Style[] => {
	const [strokeColor = '', fillColor = ''] = colors;
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkWhite,
			stroke: new Stroke({
				color: strokeColor,
				width: 3,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkColor,
			stroke: new Stroke({
				color: fillColor,
				width: 2,
			}),
		}),
	];
};


const getBaselineLinkStyle = (item: Baseline, hovered: boolean): Style[] => {
	const color = MapHelpers.getBaselineColor(item);
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkBackground,
			stroke: new Stroke({
				color: '#ffffff01',
				width: 12,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkWhite,
			stroke: new Stroke({
				color: '#ffffff',
				width: hovered ? 5 : 3,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkColor,
			stroke: new Stroke({
				color: color,
				width: hovered ? 4 : 2,
			}),
		}),
	];
};


const getArrowHeadStyle = (start: LonLat, end: LonLat, color: string): Style[] => {
	const startCoords = MapHelpers.transformCoords(start);
	const endCoords = MapHelpers.transformCoords(end);
	const endY = endCoords[1] ?? 0;
	const startY = startCoords[1] ?? 0;
	const endX = endCoords[0] ?? 0;
	const startX = startCoords[0] ?? 0;
	const angle = Math.atan2(endY - startY, endX - startX);

	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkArrow,
			image: new RegularShape({
				fill: new Fill({
					color: color,
				}),
				points: 3,
				radius: 9,
				scale: [0.7, 1],
				displacement: [0, -14],
				rotation: Math.PI / 2 - angle,
				angle: 0,
			}),
		}),
	];
};


const getBaselineSelfCircleStyle = (item: Baseline, hovered = false): Style[] => {
	const color = MapHelpers.getBaselineColor(item);
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkBackground,
			image: new Circle({
				stroke: new Stroke({
					color: '#ffffff01',
					width: 12,
				}),
				displacement: [0, 20],
				radius: 20,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkWhite,
			image: new Circle({
				stroke: new Stroke({
					color: '#ffffff',
					width: hovered ? 5 : 3,
				}),
				displacement: [0, 20],
				radius: 20,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkColor,
			image: new Circle({
				stroke: new Stroke({
					color: color,
					width: hovered ? 4 : 2,
				}),
				displacement: [0, 20],
				radius: 20,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.baselineLinkArrow,
			image: new RegularShape({
				fill: new Fill({
					color: color,
				}),
				points: 3,
				radius: 9,
				scale: [0.7, 1],
				displacement: [-2, -13],
				rotation: -5.5 / 8 * Math.PI,
				angle: 0,
			}),
		}),
	];
};


const getRoverHistoryRoverStyle = (quality: number, isCurrent: boolean, hovered: boolean): Style[] => {
	const color = '#' + MapHelpers.getGnssFixQualityDisplay(quality).color;
	const styles: Style[] = [];
	styles.push(new Style({
		zIndex: MapConsts.Z_INDEX.roverHistoryCurrentBackground,
		image: new RegularShape({
			fill: new Fill({
				color: 'black',
			}),
			stroke: new Stroke({
				color: '#00000044',
				width: 2,
			}),
			radius: hovered ? 12 : 9 + (isCurrent ? 3 : 0),
			points: 3,
			rotation: 0,
			angle: 0,
		}),
	}));
	styles.push(new Style({
		zIndex: MapConsts.Z_INDEX.roverHistoryCurrentMain,
		image: new RegularShape({
			stroke: new Stroke({
				color: Consts.COLORS.featureStrokeColor,
				width: 2,
			}),
			fill: new Fill({
				color: color,
			}),
			radius: hovered ? 10 : 7 + (isCurrent ? 3 : 0),
			points: 3,
			rotation: 0,
			angle: 0,
		}),
	}));
	return styles;
};


const getRoverHistoryFragmentDotStyle = (quality: number, isBreak: boolean, hovered: boolean): Style[] => {
	const color = '#' + MapHelpers.getGnssFixQualityDisplay(quality).color;
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.roverHistoryFragmentBreakEnd,
			image: new Circle({
				fill: new Fill({ color }),
				radius: (isBreak ? 4 : 2) + (hovered ? 2 : 0),
			}),
		}),
	];
};


const getRoverHistoryFragmentLineStyle = (quality: number, hovered: boolean): Style[] => {
	const color = '#' + MapHelpers.getGnssFixQualityDisplay(quality).color;
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.roverHistoryFragmentBackground,
			stroke: new Stroke({
				color: '#00000000',
				width: 5,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.roverHistoryFragmentBackground,
			stroke: new Stroke({
				color: Consts.COLORS.featureStrokeColor,
				width: hovered ? 5 : 3,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.roverHistoryFragmentMain,
			stroke: new Stroke({
				color: color,
				width: hovered ? 4 : 2,
			}),
		}),
	];
};


const getActiveGetterStyle = (
	service: ServiceKey,
	connectedBaseStation: ConnectedBaseStation | null,
	hovered: boolean,
): Style[] => {
	const colors = MapHelpers.getServiceColors(service, connectedBaseStation !== null, true);
	const styles: Style[] = [];
	if (connectedBaseStation !== null) {
		styles.push(new Style({
			zIndex: MapConsts.Z_INDEX.activeGetterBackground,
			image: new RegularShape({
				fill: new Fill({
					color: 'black',
				}),
				stroke: new Stroke({
					color: '#00000044',
					width: 2,
				}),
				radius: hovered ? 15 : 12,
				points: 3,
				rotation: 0,
				angle: 0,
			}),
		}));
	}
	const [strokeColor = '', fillColor = ''] = colors;
	styles.push(new Style({
		zIndex: MapConsts.Z_INDEX.activeGetterMain,
		image: new RegularShape({
			stroke: new Stroke({
				color: strokeColor,
				width: 2,
			}),
			fill: new Fill({
				color: fillColor,
			}),
			radius: hovered ? 13 : 10,
			points: 3,
			rotation: 0,
			angle: 0,
		}),
	}));
	return styles;
};


const getFailedGetterStyle = (service: ServiceKey, isNew: boolean, hovered: boolean): Style[] => {
	const colors = MapHelpers.getServiceColors(service, false, isNew);
	const [strokeColor = '', fillColor = ''] = colors;
	return [new Style({
		zIndex: MapConsts.Z_INDEX.failedGetter,
		image: new RegularShape({
			stroke: new Stroke({
				color: strokeColor,
				width: 2,
			}),
			fill: new Fill({
				color: fillColor,
			}),
			radius: hovered ? 13 : 10,
			points: 3,
			rotation: Math.PI,
			angle: 0,
		}),
	})];
};


const getBaseStationStyle = (
	statusBadge: BadgeStatusKey,
	hovered: boolean,
	selected: boolean,
	isFocused: boolean,
	isVirtual: boolean,
	reliableCoords: boolean,
): Style[] => {
	let color: string = getStatusFillColor(statusBadge);
	if (selected || (!reliableCoords && statusBadge !== 'default')) {
		color = appliedPrimaryColor.value;
	}
	let backgroundZIndex: number;
	let mainZIndex: number;
	if (isVirtual) {
		backgroundZIndex = MapConsts.Z_INDEX.vrsStationBackground;
		mainZIndex = MapConsts.Z_INDEX.vrsStationMain;
		color = Consts.COLORS.vrsServiceColor;
	} else {
		backgroundZIndex = isFocused ? MapConsts.Z_INDEX.baseStationBackgroundFocused : MapConsts.Z_INDEX.baseStationBackground;
		mainZIndex = isFocused ? MapConsts.Z_INDEX.baseStationMainFocused : MapConsts.Z_INDEX.baseStationMain;
	}
	const styles: Style[] = [
		new Style({
			zIndex: backgroundZIndex,
			image: new Circle({
				fill: new Fill({
					color: '#00000044',
				}),
				stroke: new Stroke({
					color: '#00000044',
					width: 2,
				}),
				radius: hovered ? 11 : 8,
			}),
		}),
		new Style({
			zIndex: mainZIndex,
			image: new Circle({
				fill: new Fill({
					color: color,
				}),
				stroke: new Stroke({
					color: 'white',
					width: 2,
				}),
				radius: hovered ? 10 : 7,
			}),
		}),
	];
	if (isVirtual && hovered) {
		styles.push(getTextStyle(isFocused ? MapConsts.Z_INDEX.textLabelFocused : MapConsts.Z_INDEX.textLabel, 'VRS', 23));
	}
	return styles;
};


const getBaseStationSubnetworkCirclesStyle = (colors: readonly string[]): Style[] => {
	const styles = colors.flatMap((color, index) => {
		return [new Style({
			zIndex: MapConsts.Z_INDEX.subnetworkCircleBase + 1 + index * 2,
			image: new Circle({
				stroke: new Stroke({
					color: '#00000066',
					width: 6,
				}),
				radius: 15 + index * 8,
			}),
		}), new Style({
			zIndex: MapConsts.Z_INDEX.subnetworkCircleBase + 2 + index * 2,
			image: new Circle({
				stroke: new Stroke({
					color: color,
					width: 4,
				}),
				radius: 15 + index * 8,
			}),
		})];
	});
	return styles;
};


const getBaseStationStyleGradientScheme = (
	metricValue: number | null | undefined,
	hovered: boolean,
	selected: boolean,
	isFocused: boolean,
	colorConverter: (value: number) => number,
	radiusConverter: (value: number) => number,
	displayValue: string | null | undefined,
): Style[] => {
	let color: string = Consts.COLORS.successDark;
	if (selected || hovered) {
		color = appliedPrimaryColor.value;
	}
	let radius: number;
	if (metricValue == null) {
		color = 'black';
		radius = 7;
	} else {
		color = MapHelpers.getColor(metricValue, colorConverter, MapConsts.SPRING_COLORS);
		radius = radiusConverter(metricValue);
	}
	if (hovered) {
		radius += 3;
	}
	const styles: Style[] = [
		new Style({
			zIndex: isFocused ? MapConsts.Z_INDEX.baseStationGradientBackgroundFocused : MapConsts.Z_INDEX.baseStationGradientBackground,
			image: new Circle({
				fill: new Fill({
					color: '#00000044',
				}),
				stroke: new Stroke({
					color: '#00000044',
					width: 2,
				}),
				radius: radius + 1,
			}),
		}),
		new Style({
			zIndex: isFocused ? MapConsts.Z_INDEX.baseStationGradientMainFocused : MapConsts.Z_INDEX.baseStationGradientMain,
			image: new Circle({
				fill: new Fill({
					color: color,
				}),
				stroke: new Stroke({
					color: 'white',
					width: 2,
				}),
				radius: radius,
			}),
		}),
	];
	const finalDisplayValue = (metricValue == null) ? 'N/A' : (displayValue || Utils.formatFractions(2)(metricValue));
	styles.push(getTextStyle(isFocused ? MapConsts.Z_INDEX.textLabelGradientStyleFocused : MapConsts.Z_INDEX.textLabelGradientStyle, finalDisplayValue, 40));
	return styles;
};


const createBaseStationSpoofingOverlayFillColor = (): CanvasPattern | string => {
	if (typeof document === 'undefined') {
		return MapConsts.GSS_HATCH_FILL_FALLBACK;
	}
	const canvas = document.createElement('canvas');
	canvas.width = MapConsts.BASE_STATION_SPOOFING_OVERLAY_PATTERN_SIZE_PX;
	canvas.height = MapConsts.BASE_STATION_SPOOFING_OVERLAY_PATTERN_SIZE_PX;
	const ctx = canvas.getContext('2d');
	if (ctx == null) {
		return MapConsts.GSS_HATCH_FILL_FALLBACK;
	}
	ctx.fillStyle = MapConsts.BASE_STATION_SPOOFING_OVERLAY_WASH;
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.strokeStyle = MapConsts.BASE_STATION_SPOOFING_OVERLAY_STRIPE;
	ctx.lineWidth = 1.5;
	ctx.beginPath();
	ctx.moveTo(-2, 6);
	ctx.lineTo(6, -2);
	ctx.moveTo(2, 10);
	ctx.lineTo(10, 2);
	ctx.stroke();
	return ctx.createPattern(canvas, 'repeat') ?? MapConsts.GSS_HATCH_FILL_FALLBACK;
};

const BASE_STATION_SPOOFING_OVERLAY_STYLE = new Style({
	zIndex: MapConsts.Z_INDEX.baseStationSpoofingOverlay,
	image: new Circle({
		radius: MapConsts.BASE_STATION_SPOOFING_OVERLAY_RADIUS_PX,
		fill: new Fill({
			color: createBaseStationSpoofingOverlayFillColor(),
		}),
	}),
});

const shouldShowBaseStationSpoofingOverlay = (
	mapMode: MapMode,
	featureData: BaseStationStyleFeatureData,
): boolean => (
	mapMode === MAP_MODE.main
	&& featureData.status === 'working'
	&& (featureData.gssFailingGnss ?? []).length > 0
);

const addSpoofingOverlay = (styles: Style[]): Style[] => (
	[...styles, BASE_STATION_SPOOFING_OVERLAY_STYLE]
);

const getBaseStationStyleMain = (
	mapMode: MapMode,
	featureType: typeof FEATURE_TYPE.baseStation | typeof FEATURE_TYPE.vrsStation,
	featureData: BaseStationStyleFeatureData,
	hovered: boolean,
	selected: boolean,
	isFocused: boolean,
): Style[] => {
	if (featureType === FEATURE_TYPE.baseStation) {
		// пришлось оставить str, а не поля объекта MAP_MODE, потому что циклические зависимости
		if (mapMode === 'paasStddev') {
			const daysStr = featureData.paasStddev?.days === 1 ? 'day' : 'days';
			const displayValue = (featureData.paasStddev == null)
				? null
				: (Utils.formatFractions(2)(featureData.paasStddev.value) + ' mm / ' + featureData.paasStddev.days + ' ' + daysStr);
			return getBaseStationStyleGradientScheme(
				featureData.paasStddev?.value,
				hovered,
				selected,
				isFocused,
				MapConsts.paasStddevColorConverter,
				MapConsts.paasStddevRadiusConverter,
				displayValue,
			);
		}
	}
	if (featureType === FEATURE_TYPE.vrsStation) {
		return getBaseStationStyle(
			'default',
			hovered,
			selected,
			false,
			true,
			true,
		);
	}
	let badge: BadgeStatusKey = featureData.statusLiveData.badge ? featureData.statusLiveData.badge : featureData.statusOnlineData.badge;
	if (featureData.vrsAccuracyData) {
		badge = featureData.vrsAccuracyData.badge;
	}
	const styles = getBaseStationStyle(
		badge,
		hovered,
		selected,
		isFocused,
		false,
		featureData.reliable_coords,
	);
	if (shouldShowBaseStationSpoofingOverlay(mapMode, featureData)) {
		return addSpoofingOverlay(styles);
	}
	return styles;
};


const getCircleStyle = (status: BadgeStatusKey, isVirtual: boolean, fillCoverageArea: boolean): Style[] => {
	let color: string = getStatusFillColor(status);
	if (isVirtual) {
		color = Consts.COLORS.vrsServiceColor;
	}
	return [
		new Style({
			zIndex: isVirtual ? MapConsts.Z_INDEX.vrsStationCircle : MapConsts.Z_INDEX.baseStationCircle,
			fill: new Fill({
				color: fillCoverageArea ? color + '44' : color + '00',
			}),
			stroke: new Stroke({
				color: color + 'bb',
				width: fillCoverageArea ? 1 : 2,
			}),
		}),
	];
};


const getBaseStationLabelOpacity = (mapMode: MapMode, fillCoverageArea: boolean): number => {
	if (mapMode !== MAP_MODE.gnssPerGridpoint) {
		return 1;
	}
	return fillCoverageArea ? 0.5 : 0;
};


const getLabelStyle = (name: string | null | undefined, isFocused: boolean, opacity = 1): Style[] => {
	if (!name || opacity === 0) {
		return [];
	}
	return [getTextStyle(isFocused ? MapConsts.Z_INDEX.textLabelFocused : MapConsts.Z_INDEX.textLabel, name, 23, opacity)];
};


const getGridpointStyle = (label: string): Style[] => {
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.baseStationGradientBackground,
			image: new Circle({
				fill: new Fill({
					color: '#00000099',
				}),
				stroke: new Stroke({
					color: '#ffffffff',
					width: 2,
				}),
				radius: 4,
			}),
		}),
		getTextStyle(MapConsts.Z_INDEX.textLabel, label, 18),
	];
};


const getCnetConstructorGridpointStyle = (selected: boolean, hovered: boolean, gpIndex: number): Style[] => {
	let radius = selected ? 7 : 4;
	if (hovered) {
		radius = 9;
	}
	const styles: Style[] = [
		new Style({
			zIndex: MapConsts.Z_INDEX.cnetConstructorGridpointBackground,
			image: new Circle({
				fill: new Fill({
					color: '#00000000',
				}),
				radius: 9,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.cnetConstructorGridpointMain,
			image: new Circle({
				fill: new Fill({
					color: selected ? '#000000' : '#999999',
				}),
				stroke: new Stroke({
					color: '#ffffffff',
					width: 2,
				}),
				radius,
			}),
		}),
	];
	if (gpIndex > 0) {
		const formattedIndex = new Intl.NumberFormat('en-US', {
			minimumIntegerDigits: 2,
			useGrouping: false,
		}).format(gpIndex);
		styles.push(getTextStyle(MapConsts.Z_INDEX.textLabel, 'gp' + formattedIndex, 18));
	}
	return styles;
};


const getCnetConstructorOtherGridpointStyle = (label: string, color: string): Style[] => {
	const styles: Style[] = [
		new Style({
			zIndex: MapConsts.Z_INDEX.cnetConstructorGridpointBackground,
			image: new Circle({
				fill: new Fill({
					color: '#00000000',
				}),
				radius: 9,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.cnetConstructorGridpointMain,
			image: new Circle({
				fill: new Fill({
					color: color,
				}),
				stroke: new Stroke({
					color: '#ffffffff',
					width: 2,
				}),
				radius: 4,
			}),
		}),
		getTextStyle(MapConsts.Z_INDEX.textLabel, label, 18),
	];
	return styles;
};


const getAnchorPointStyle = (): Style[] => {
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.anchorPoint,
			image: new RegularShape({
				fill: new Fill({
					color: '#ee9900',
				}),
				stroke: new Stroke({
					color: '#eeaa00',
					width: 4,
				}),
				points: 4,
				radius: 14,
				radius2: 0,
				angle: 0,
			}),
		}),
	];
};


const getCoverageContourStyle = (): Style[] => {
	return [
		new Style({
			zIndex: MapConsts.Z_INDEX.coverageContour,
			fill: new Fill({
				color: '#2f54eb22',
			}),
			stroke: new Stroke({
				color: '#2f54ebcc',
				width: 2,
			}),
		}),
	];
};


const getQualityRectangleStyle = (value: number, hovered: boolean): Style[] => {
	const styleOptions: StyleOptions = {
		zIndex: MapConsts.Z_INDEX.qualityRectangle,
		fill: new Fill({
			color: MapHelpers.getQualityColor(value),
		}),
	};
	if (hovered) {
		styleOptions.stroke = new Stroke({
			color: '#ffffff',
			width: 2,
		});
	}
	return [new Style(styleOptions)];
};


export default {
	getTextStyle,
	getLinkStyle,
	getBaselineLinkStyle,
	getArrowHeadStyle,
	getBaselineSelfCircleStyle,
	getRoverHistoryRoverStyle,
	getRoverHistoryFragmentDotStyle,
	getRoverHistoryFragmentLineStyle,
	getActiveGetterStyle,
	getFailedGetterStyle,
	getBaseStationStyle,
	getBaseStationSubnetworkCirclesStyle,
	getBaseStationStyleGradientScheme,
	getBaseStationStyleMain,
	getCircleStyle,
	getBaseStationLabelOpacity,
	getLabelStyle,
	getGridpointStyle,
	getCnetConstructorGridpointStyle,
	getCnetConstructorOtherGridpointStyle,
	getAnchorPointStyle,
	getCoverageContourStyle,
	getQualityRectangleStyle,
};
